import React, { useEffect, useState, useCallback } from 'react'
import { Plus, Edit2, X } from 'lucide-react'
import {
  fetchSectionsByRestaurant,
  deleteRestaurantSection,
  FrontendRestaurantSection,
} from '../../services/restaurant/restaurantSectionService'
import { getRestaurantSectionTypeDisplayName } from '../../types/RestaurantSectionType'
import SaveRestaurantSectionModal from './SaveRestaurantSectionModal'
import { RestaurantPlatformsList } from './RestaurantPlatformsList'

interface Props {
  restaurantId: string
}

export const RestaurantSectionsList: React.FC<Props> = ({ restaurantId }) => {
  const [sections, setSections] = useState<FrontendRestaurantSection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [selectedSection, setSelectedSection] = useState<FrontendRestaurantSection | null>(null)
  const [deletingSection, setDeletingSection] = useState<FrontendRestaurantSection | null>(null)

  const loadSections = useCallback(async () => {
    try {
      setLoading(true)
      const sectionsData = await fetchSectionsByRestaurant(restaurantId)
      setSections(sectionsData)
      setError(null)
    } catch (err: any) {
      console.error('Failed to load sections:', err)
      setError('Failed to load sections')
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    loadSections()
  }, [loadSections])

  const handleCreate = () => {
    setModalMode('create')
    setSelectedSection(null)
    setModalOpen(true)
  }

  const handleEdit = (section: FrontendRestaurantSection) => {
    setModalMode('edit')
    setSelectedSection(section)
    setModalOpen(true)
  }

  const handleDelete = async (section: FrontendRestaurantSection) => {
    if (sections.length <= 1) {
      alert('Cannot delete the last section from a restaurant')
      return
    }

    setDeletingSection(section)
  }

  const confirmDelete = async () => {
    if (!deletingSection) return

    try {
      await deleteRestaurantSection(deletingSection.id)
      await loadSections()
      setDeletingSection(null)
    } catch (err: any) {
      console.error('Failed to delete section:', err)
      const errorMessage = err.message || 'Failed to delete section'
      alert(errorMessage)
    }
  }

  if (loading) {
    return <p className="text-muted dark:text-muted-dark">Loading sections...</p>
  }

  if (error) {
    return <p className="text-destructive dark:text-destructive-dark">{error}</p>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-50">Restaurant Sections</h2>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Section
        </button>
      </div>

      {sections.length > 0 ? (
        <div className="space-y-6">
          {sections.map(section => (
            <div
              key={section.id}
              className="bg-card dark:bg-card-dark rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6 hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors space-y-5"
            >
              {/* Section Header */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{section.name}</h3>
                    <span className="px-2 py-1 text-xs font-medium rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                      {getRestaurantSectionTypeDisplayName(section.restaurantSectionType)}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                    Created {new Date(section.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(section)}
                    className="p-2 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
                    aria-label="Edit section"
                  >
                    <Edit2 className="w-4 h-4 text-zinc-700 dark:text-zinc-300" />
                  </button>
                  {sections.length > 1 && (
                    <button
                      onClick={() => handleDelete(section)}
                      className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                      aria-label="Delete section"
                    >
                      <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Platforms */}
              <div>
                <RestaurantPlatformsList restaurantSectionId={section.id} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-zinc-200 dark:border-zinc-700 rounded-2xl">
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            No sections yet. Add one to get started.
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add First Section
          </button>
        </div>
      )}

      <SaveRestaurantSectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        restaurantId={restaurantId}
        initialId={selectedSection?.id}
        initialName={selectedSection?.name}
        initialRestaurantSectionType={selectedSection?.restaurantSectionType}
        onSave={loadSections}
      />

      {/* Delete Confirmation Modal */}
      {deletingSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={() => setDeletingSection(null)}>
          <div
            className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50">
                  Delete Section?
                </h2>
                <button
                  onClick={() => setDeletingSection(null)}
                  className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-zinc-600 dark:text-zinc-400">
                  Are you sure you want to delete the section "{deletingSection.name}"? All associated platforms will be removed.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingSection(null)}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-700 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-800 transition"
                >
                  Delete Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}